import React, { useState, useEffect } from 'react';
import {
  Database,
  Users,
  Plus,
  Activity,
  FileText,
  Server,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { api } from '../services/api';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { Input } from '../components/common/Input';

export const DashboardHome: React.FC = () => {
  const { user, tenant } = useAuth();
  const { subdomain } = useTenant();

  const [dashboardData, setDashboardData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New Record Form State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState('Operations');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchDashboard = async () => {
    setIsLoading(true);
    try {
      const data = await api.getDashboardData();
      setDashboardData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load tenant dashboard data.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [subdomain]);

  const handleCreateRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    setIsSubmitting(true);
    try {
      await api.createRecord({
        title: newTitle.trim(),
        category: newCategory,
        data: { createdVia: 'Vite PWA Dashboard', timestamp: new Date().toISOString() },
      });
      setNewTitle('');
      setShowCreateModal(false);
      await fetchDashboard();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="animate-spin" style={{ width: '40px', height: '40px', border: '3px solid rgba(99, 102, 241, 0.2)', borderTopColor: 'var(--brand-primary)', borderRadius: '50%', margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text-secondary)' }}>Querying isolated tenant database...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {
    totalUsers: 1,
    totalRecords: 1,
    storageUsedMb: '0.1',
    tierMaxUsers: 10,
    tierStorageLimitGb: 5,
  };

  const records = dashboardData?.records || [];
  const members = dashboardData?.recentMembers || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {error && (
        <div
          style={{
            padding: '14px 18px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-danger-bg)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#fca5a5',
            fontSize: '0.875rem',
          }}
        >
          {error}
        </div>
      )}
      {/* Workspace Banner */}
      <div
        className="glass-panel glass-panel-glow"
        style={{
          padding: '28px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <h1 style={{ fontSize: '1.75rem' }}>Welcome back, {user?.firstName || 'Admin'}</h1>
            <Badge variant="primary">{tenant?.tier || 'Enterprise'} Tier</Badge>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
            Workspace: <strong style={{ color: '#fff' }}>{tenant?.name || subdomain}</strong> &nbsp;•&nbsp; 
            Database: <code style={{ color: '#a5b4fc', fontFamily: 'var(--font-mono)' }}>{dashboardData?.tenant?.dbName || `tenant_${subdomain}_db`}</code>
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowCreateModal(true)}
          >
            Create Tenant Record
          </Button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                DATABASE STATUS
              </span>
              <h3 style={{ fontSize: '1.5rem', marginTop: '6px', color: 'var(--color-success)' }}>
                Isolated & Active
              </h3>
            </div>
            <div style={{ padding: '10px', borderRadius: '12px', background: 'var(--color-success-bg)', color: 'var(--color-success)' }}>
              <Server className="w-5 h-5" />
            </div>
          </div>
          <div style={{ marginTop: '16px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Physical/logical DB: <span style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{dashboardData?.tenant?.dbName || `tenant_${subdomain}_db`}</span>
          </div>
        </Card>

        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                ORGANIZATION MEMBERS
              </span>
              <h3 style={{ fontSize: '1.5rem', marginTop: '6px' }}>
                {stats.totalUsers} <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>/ {stats.tierMaxUsers}</span>
              </h3>
            </div>
            <div style={{ padding: '10px', borderRadius: '12px', background: 'var(--brand-primary-light)', color: 'var(--brand-primary)' }}>
              <Users className="w-5 h-5" />
            </div>
          </div>
          <div style={{ marginTop: '16px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Role-based access in tenant context
          </div>
        </Card>

        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                BUSINESS RECORDS
              </span>
              <h3 style={{ fontSize: '1.5rem', marginTop: '6px' }}>
                {stats.totalRecords}
              </h3>
            </div>
            <div style={{ padding: '10px', borderRadius: '12px', background: 'rgba(14, 165, 233, 0.12)', color: 'var(--color-info)' }}>
              <Database className="w-5 h-5" />
            </div>
          </div>
          <div style={{ marginTop: '16px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Isolated in tenant MongoDB collection
          </div>
        </Card>

        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                CENTRAL ANALYTICS SYNC
              </span>
              <h3 style={{ fontSize: '1.5rem', marginTop: '6px', color: '#a855f7' }}>
                Live Stream
              </h3>
            </div>
            <div style={{ padding: '10px', borderRadius: '12px', background: 'rgba(168, 85, 247, 0.12)', color: '#a855f7' }}>
              <Activity className="w-5 h-5" />
            </div>
          </div>
          <div style={{ marginTop: '16px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            De-identified telemetry piped to Central DB
          </div>
        </Card>
      </div>

      {/* Main Content Grid: Records Table + Team Panel */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        {/* Isolated Business Records Table */}
        <div className="glass-panel" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3 style={{ fontSize: '1.125rem' }}>Tenant Business Records</h3>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                These records exist strictly within <code style={{ color: '#a5b4fc' }}>{dashboardData?.tenant?.dbName}</code>
              </p>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setShowCreateModal(true)}>
              + Add Record
            </Button>
          </div>

          {records.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
              <FileText className="w-8 h-8" style={{ margin: '0 auto 8px', opacity: 0.5 }} />
              <p>No records found in this tenant database yet.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                    <th style={{ padding: '10px 12px' }}>Title</th>
                    <th style={{ padding: '10px 12px' }}>Category</th>
                    <th style={{ padding: '10px 12px' }}>Status</th>
                    <th style={{ padding: '10px 12px' }}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((rec: any) => (
                    <tr
                      key={rec.id || rec._id}
                      style={{ borderBottom: '1px solid var(--border-subtle)', transition: 'background var(--transition-fast)' }}
                    >
                      <td style={{ padding: '14px 12px', fontWeight: 600 }}>{rec.title}</td>
                      <td style={{ padding: '14px 12px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: '4px', background: 'rgba(255,255,255,0.06)', fontSize: '0.75rem' }}>
                          {rec.category}
                        </span>
                      </td>
                      <td style={{ padding: '14px 12px' }}>
                        <Badge variant={rec.status === 'published' ? 'success' : 'warning'}>
                          {rec.status}
                        </Badge>
                      </td>
                      <td style={{ padding: '14px 12px', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                        {new Date(rec.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Organization Members Sidebar */}
        <div className="glass-panel" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '1.125rem', marginBottom: '8px' }}>Tenant Team</h3>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Users authenticated against this tenant DB.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {members.map((m: any) => (
              <div
                key={m._id || m.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgba(15, 23, 42, 0.5)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'var(--brand-primary-light)',
                    color: 'var(--brand-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                  }}
                >
                  {m.firstName?.[0] || 'U'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.firstName} {m.lastName}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.email}
                  </div>
                </div>
                <Badge variant={m.role === 'owner' ? 'primary' : 'success'} style={{ fontSize: '0.65rem' }}>
                  {m.role}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Create Record Modal */}
      {showCreateModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '20px',
          }}
        >
          <div className="glass-panel" style={{ width: '100%', maxWidth: '460px', padding: '28px' }}>
            <h3 style={{ fontSize: '1.25rem', marginBottom: '8px' }}>Create Isolated Tenant Record</h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '20px' }}>
              This document will be saved directly into <code style={{ color: '#a5b4fc' }}>{dashboardData?.tenant?.dbName}</code>.
            </p>

            <form onSubmit={handleCreateRecord}>
              <Input
                label="Record Title"
                placeholder="e.g., Q3 Financial Operations Report"
                required
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />

              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-input"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                >
                  <option value="Operations">Operations</option>
                  <option value="Finance">Finance</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Customer Data">Customer Data</option>
                </select>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <Button type="button" variant="ghost" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={isSubmitting}>
                  Save to Isolated DB
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
